import { get } from 'lodash-es';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { IoIosStats } from 'react-icons/io';
import { FcDataConfiguration } from 'react-icons/fc';
import { MdPerson } from 'react-icons/md';
import { connect, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Button, Collapse, Nav, Navbar, NavbarBrand, NavbarToggler, NavItem, Popover, PopoverBody, PopoverHeader,
    Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import atLogo from '../../../assets/img/at_logo.png';

import { IS_LOGIN_NOT_REQUIRED, logout } from '../../../auth';
import { updateControlDetailView, updateMainView, updateSecondaryPanelView, updateActiveControlEntityId } from '../../../redux/actions/navigation';
import { isAlertsEmpty } from '../../../redux/selectors/control/alerts';
import { getActiveControlDetailView, getActiveMainView, getActiveSecondaryPanelView, getQueryParams, getActiveControlEntityId } from '../../../redux/selectors/navigation';
import { getStopMessagesPermissions } from '../../../redux/selectors/control/stopMessaging/stopMessages';
import { getStopMessagesAndPermissions } from '../../../redux/actions/control/stopMessaging';
import { isGlobalEditStopMessagesPermitted } from '../../../utils/user-permissions';
import { getUserPermissions, getUserProfile } from '../../../redux/selectors/user';
import VIEW_TYPE from '../../../types/view-types';
import { IS_ALERTS_ENABLED, IS_DISRUPTIONS_ENABLED, IS_TRIP_REPLAYS_ENABLED, IS_ANALYTICS_ENABLED, IS_FLEETS_ENABLED } from '../../../utils/feature-toggles';
import CustomButton from '../../Common/CustomButton/CustomButton';
import Icon from '../../Common/Icon/Icon';
import { HelpInformationModal } from '../HelpInformationModal/HelpInformationModal';
import { resetRealtimeToDefault } from '../../../redux/actions/realtime/common';
import { useBusPriorityDataManagement, useRecurringCancellationsGridView, useParentChildIncident } from '../../../redux/selectors/appSettings';
import { isDisruptionFormModified as selectIsDisruptionFormModified } from '../../../redux/selectors/control/disruptions';
import { goToNotificationsView } from '../../../redux/actions/control/link';
import { openCreateDisruption, updateIsDisruptionFormModified } from '../../../redux/actions/control/disruptions';
import { openCreateIncident } from '../../../redux/actions/control/incidents';
import CustomModal from '../../Common/CustomModal/CustomModal';
import LeaveFormConfirmation from '../../Common/LeaveFormConfirmation/LeaveFormConfirmation';

import './Header.scss';

export function Header(props) {
    const location = useLocation();
    const history = useHistory();
    const isDisruptionFormModified = useSelector(selectIsDisruptionFormModified);
    const [isOpen, setIsOpen] = useState(false);
    const [isUserPopoverOpen, setIsUserPopoverOpen] = useState(false);
    const [isHelpModalVisible, setIsHelpModalVisible] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [isDropdownHovered, setDropdownHovered] = useState(false);
    const [leaveFormConfirm, setLeaveFormConfirm] = useState({ open: false, onDiscard: null });

    const isGlobalEditMessagesPermitted = IS_LOGIN_NOT_REQUIRED || isGlobalEditStopMessagesPermitted(props.stopMessagesPermissions);

    const toggle = () => {
        setDropdownOpen(prevState => !prevState);
    };

    const dropDownHovered = () => {
        setDropdownHovered(prevState => !prevState);
    };

    const handleUrlChange = (inputLocation) => {
        if (inputLocation.pathname === '/') {
            props.updateMainView(VIEW_TYPE.MAIN.REAL_TIME);
            props.updateControlDetailView();
        } else {
            const paths = inputLocation.pathname.split('/');
            props.updateMainView(paths[1]);
            if (paths[1] === VIEW_TYPE.MAIN.REAL_TIME) {
                props.updateControlDetailView();
            } else {
                props.updateControlDetailView(paths[2]);
                props.updateActiveControlEntityId(paths[3]);
            }
        }
    };

    /**
     * When form has unsaved changes, shows confirm modal instead of navigating.
     * Reuses same copy as Cancellation (disruption/incident wizard).
     */
    const requestNavigate = (doNavigate) => {
        if (isDisruptionFormModified) {
            setLeaveFormConfirm({ open: true, onDiscard: doNavigate });
        } else {
            doNavigate();
        }
    };

    const closeLeaveFormConfirm = () => {
        if (document.activeElement?.blur) {
            document.activeElement.blur();
        }
        setLeaveFormConfirm({ open: false, onDiscard: null });
    };

    const discardAndNavigate = () => {
        props.updateIsDisruptionFormModified(false);
        props.openCreateIncident(false);
        props.openCreateDisruption(false);
        if (leaveFormConfirm.onDiscard) {
            leaveFormConfirm.onDiscard();
        }
        closeLeaveFormConfirm();
    };

    /**
     * Changes the current view; if disruption form is modified, shows confirm modal first.
     */
    const handleChangeView = (view) => {
        requestNavigate(() => {
            props.updateMainView(VIEW_TYPE.MAIN.CONTROL);
            props.updateControlDetailView(view);
            if (view === VIEW_TYPE.CONTROL_DETAIL.DISRUPTIONS || view === VIEW_TYPE.CONTROL_DETAIL.INCIDENTS) {
                props.updateActiveControlEntityId(undefined);
            }
        });
    };

    const toggleHelpModal = () => {
        setIsHelpModalVisible(!isHelpModalVisible);
    };

    useEffect(() => {
        props.getStopMessagesAndPermissions();
        handleUrlChange(location);
        const removeBackListener = history.listen((currentLocation, action) => {
            if (action === 'POP') {
                handleUrlChange(currentLocation);
            }
        });
        return () => {
            removeBackListener();
        };
    }, []);

    useEffect(() => {
        const entityId = props.activeControlEntityId ? `/${props.activeControlEntityId}` : '';
        const stringQueryParams = props.controlActiveView === VIEW_TYPE.CONTROL_DETAIL.NOTIFICATIONS && props.queryParams ? `?${new URLSearchParams(props.queryParams).toString()}` : '';
        const locationToPush = `/${props.activeView}/${props.controlActiveView}${entityId}${stringQueryParams}`;
        if (locationToPush !== location.pathname
            && [
                VIEW_TYPE.CONTROL_DETAIL.BLOCKS,
                VIEW_TYPE.CONTROL_DETAIL.ROUTES,
                VIEW_TYPE.CONTROL_DETAIL.STOP_MESSAGES,
                props.useParentChildIncident ? VIEW_TYPE.CONTROL_DETAIL.INCIDENTS : VIEW_TYPE.CONTROL_DETAIL.DISRUPTIONS,
                VIEW_TYPE.CONTROL_DETAIL.ALERTS,
                VIEW_TYPE.CONTROL_DETAIL.FLEETS,
                VIEW_TYPE.CONTROL_DETAIL.RECURRING_CANCELLATIONS,
                VIEW_TYPE.CONTROL_DETAIL.DATA_MANAGEMENT,
                VIEW_TYPE.CONTROL_DETAIL.NOTIFICATIONS,
            ].includes(props.controlActiveView)) {
            history.push(locationToPush);
        }
    }, [props.controlActiveView, props.activeControlEntityId]);

    useEffect(() => {
        const locationToPush = props.activeView === VIEW_TYPE.MAIN.REAL_TIME ? '/' : `/${props.activeView}`;
        if (locationToPush !== location.pathname && [VIEW_TYPE.MAIN.DASHBOARD, VIEW_TYPE.MAIN.ANALYTICS].includes(props.activeView)) {
            history.push(locationToPush);
        }
    }, [props.activeView]);

    const { activeView, controlActiveView, userProfile, userPermissions } = props;
    const isViewPermitted = view => IS_LOGIN_NOT_REQUIRED || get(userPermissions, view, false);

    const isRouteTabActive = () => {
        if (activeView === VIEW_TYPE.MAIN.CONTROL
            && (controlActiveView === VIEW_TYPE.CONTROL_DETAIL.ROUTES || controlActiveView === VIEW_TYPE.CONTROL_DETAIL.RECURRING_CANCELLATIONS)) {
            return true;
        }
        return isDropdownHovered;
    };

    const isDataManagementViewPermitted = () => (
        isGlobalEditMessagesPermitted
        || (props.useBusPriorityDataManagement && isViewPermitted('controlBusPriorityView')));

    return (
        <Navbar className="header at-nav-background p-0 fixed-top" expand="md" dark>
            <NavbarBrand className="m-0">
                <CustomButton
                    tabIndex="0"
                    className="header__logo--button"
                    ariaLabel="Auckland transport logo"
                    onClick={ props.resetRealtimeToDefault }>
                    <img className="header__logo p-2" src={ atLogo } alt="at.govt.nz" />
                </CustomButton>
            </NavbarBrand>
            <NavbarToggler onClick={ () => setIsOpen(!isOpen) } />
            <Collapse isOpen={ isOpen } navbar className="at-nav-background">
                <Nav className="ml-left mr-auto" navbar>
                    <NavItem>
                        <CustomButton
                            className="header__btn header__real-time rounded-0 px-3"
                            active={ activeView === VIEW_TYPE.MAIN.REAL_TIME }
                            tabIndex="0"
                            ariaLabel="Real time section button"
                            onClick={ () => requestNavigate(() => {
                                props.updateMainView(VIEW_TYPE.MAIN.REAL_TIME);
                                props.updateControlDetailView();
                            }) }>
                            REAL TIME MAP
                        </CustomButton>
                    </NavItem>
                    { isViewPermitted('controlBlocksView') && (
                        <NavItem>
                            <CustomButton
                                className="header__btn header__blocks rounded-0 px-3"
                                active={ activeView === VIEW_TYPE.MAIN.CONTROL && controlActiveView === VIEW_TYPE.CONTROL_DETAIL.BLOCKS }
                                tabIndex="0"
                                ariaLabel="Block section button"
                                onClick={ () => requestNavigate(() => {
                                    props.updateMainView(VIEW_TYPE.MAIN.CONTROL);
                                    props.updateControlDetailView(VIEW_TYPE.CONTROL_DETAIL.BLOCKS);
                                }) }>
                                BLOCKS
                            </CustomButton>
                        </NavItem>
                    )}
                    { isViewPermitted('controlRoutesView') && (
                        <div
                            onMouseEnter={ () => setDropdownOpen(true) }
                            onMouseLeave={ () => setDropdownOpen(false) }
                        >
                            <Dropdown
                                nav
                                toggle={ toggle }
                                isOpen={ dropdownOpen }>
                                <DropdownToggle nav>
                                    <CustomButton
                                        className="header__btn header__routes rounded-0 px-3"
                                        active={ isRouteTabActive() }
                                        tabIndex="0"
                                        ariaLabel="Routes section button"
                                        onClick={ () => {
                                            handleChangeView(VIEW_TYPE.CONTROL_DETAIL.ROUTES);
                                        } }>
                                        ROUTES & TRIPS
                                    </CustomButton>
                                </DropdownToggle>
                                { props.useRecurringCancellationsGridView && (
                                    <DropdownMenu
                                        className="header-dropdown at-nav-background"
                                    >
                                        <DropdownItem
                                            className="header-dropdown-item"
                                            onMouseEnter={ () => dropDownHovered() }
                                            onMouseLeave={ () => dropDownHovered() }
                                            onClick={ () => {
                                                handleChangeView(VIEW_TYPE.CONTROL_DETAIL.RECURRING_CANCELLATIONS);
                                                setDropdownHovered(false);
                                            } }
                                        >
                                            Recurring Cancellations
                                        </DropdownItem>
                                    </DropdownMenu>
                                )}
                            </Dropdown>
                        </div>
                    )}
                    { (props.useParentChildIncident && IS_DISRUPTIONS_ENABLED && isViewPermitted('controlDisruptionsView')) && (
                        <NavItem>
                            <CustomButton
                                className="header__btn header__disruption rounded-0 px-3"
                                active={ activeView === VIEW_TYPE.MAIN.CONTROL && controlActiveView === VIEW_TYPE.CONTROL_DETAIL.INCIDENTS }
                                tabIndex="0"
                                ariaLabel="Disruption section button"
                                onClick={ () => handleChangeView(VIEW_TYPE.CONTROL_DETAIL.INCIDENTS) }>
                                DISRUPTIONS
                            </CustomButton>
                        </NavItem>
                    )}
                    { (!props.useParentChildIncident && IS_DISRUPTIONS_ENABLED && isViewPermitted('controlDisruptionsView')) && (
                        <NavItem>
                            <CustomButton
                                className="header__btn header__disruption rounded-0 px-3"
                                active={ activeView === VIEW_TYPE.MAIN.CONTROL && controlActiveView === VIEW_TYPE.CONTROL_DETAIL.DISRUPTIONS }
                                tabIndex="0"
                                ariaLabel="Disruption section button"
                                onClick={ () => handleChangeView(VIEW_TYPE.CONTROL_DETAIL.DISRUPTIONS) }>
                                DISRUPTIONS
                            </CustomButton>
                        </NavItem>
                    )}
                    { isViewPermitted('controlStopMessagingView') && (
                        <NavItem>
                            <CustomButton
                                className="header__btn header__messaging rounded-0 px-3"
                                active={ activeView === VIEW_TYPE.MAIN.CONTROL && controlActiveView === VIEW_TYPE.CONTROL_DETAIL.STOP_MESSAGES }
                                tabIndex="0"
                                ariaLabel="Messaging section button"
                                onClick={ () => {
                                    handleChangeView(VIEW_TYPE.CONTROL_DETAIL.STOP_MESSAGES);
                                } }>
                                MESSAGING
                            </CustomButton>
                        </NavItem>
                    )}
                    { isViewPermitted('controlNotificationsView') && (
                        <NavItem>
                            <CustomButton
                                className="header__btn header__notifications rounded-0 px-3"
                                active={ activeView === VIEW_TYPE.MAIN.CONTROL && controlActiveView === VIEW_TYPE.CONTROL_DETAIL.NOTIFICATIONS }
                                tabIndex="0"
                                ariaLabel="Notifications button"
                                onClick={ () => requestNavigate(() => props.goToNotificationsView()) }>
                                NOTIFICATIONS
                            </CustomButton>
                        </NavItem>
                    )}
                    { (IS_TRIP_REPLAYS_ENABLED && isViewPermitted('controlTripReplaysView')) && (
                        <NavItem>
                            <CustomButton
                                className="header__btn header__trip-replays rounded-0 px-3"
                                active={ activeView === VIEW_TYPE.MAIN.CONTROL && controlActiveView === VIEW_TYPE.CONTROL_DETAIL.TRIP_REPLAYS }
                                tabIndex="0"
                                ariaLabel="Trip Replays section button"
                                onClick={ () => requestNavigate(() => {
                                    props.updateMainView(VIEW_TYPE.MAIN.CONTROL);
                                    props.updateControlDetailView(VIEW_TYPE.CONTROL_DETAIL.TRIP_REPLAYS);
                                }) }>
                                REPLAY
                            </CustomButton>
                        </NavItem>
                    )}
                    { (IS_FLEETS_ENABLED && isViewPermitted('controlFleetsView')) && (
                        <NavItem>
                            <CustomButton
                                className="header__btn header__fleets rounded-0 px-3 position-relative"
                                active={ activeView === VIEW_TYPE.MAIN.CONTROL && controlActiveView === VIEW_TYPE.CONTROL_DETAIL.FLEETS }
                                tabIndex="0"
                                ariaLabel="Fleets button"
                                onClick={ () => requestNavigate(() => {
                                    props.updateMainView(VIEW_TYPE.MAIN.CONTROL);
                                    props.updateControlDetailView(VIEW_TYPE.CONTROL_DETAIL.FLEETS);
                                }) }>
                                FLEET
                            </CustomButton>
                        </NavItem>
                    )}
                    { IS_ANALYTICS_ENABLED && (
                        <NavItem>
                            <CustomButton
                                className="header__btn rounded-0 px-3"
                                active={ activeView === VIEW_TYPE.MAIN.ANALYTICS }
                                tabIndex="0"
                                ariaLabel="Analytics section button"
                                onClick={ () => requestNavigate(() => {
                                    props.updateMainView(VIEW_TYPE.MAIN.ANALYTICS);
                                    props.updateControlDetailView();
                                }) }>
                                ANALYTICS
                            </CustomButton>
                        </NavItem>
                    )}
                    { (IS_ALERTS_ENABLED && isViewPermitted('controlAlertsView')) && (
                        <NavItem>
                            <CustomButton
                                className="header__btn header__alerts rounded-0 px-3"
                                active={ activeView === VIEW_TYPE.MAIN.CONTROL && controlActiveView === VIEW_TYPE.CONTROL_DETAIL.ALERTS }
                                tabIndex="0"
                                ariaLabel="Alerts button"
                                onClick={ () => requestNavigate(() => {
                                    props.updateMainView(VIEW_TYPE.MAIN.CONTROL);
                                    props.updateControlDetailView(VIEW_TYPE.CONTROL_DETAIL.ALERTS);
                                }) }>
                                ALERTS
                                {' '}
                                {!props.hasAlerts && (
                                    <span style={ { height: 8, width: 8, marginLeft: 3 } } className="header__alerts-badge rounded-circle position-absolute bg-warning">
                                        &nbsp;
                                    </span>
                                )}
                            </CustomButton>
                        </NavItem>
                    )}
                </Nav>
                <Nav className="header__toolbar ml-auto" navbar>
                    { isDataManagementViewPermitted() && (
                        <NavItem>
                            <CustomButton
                                className="header__btn header__data-management rounded-0 px-3"
                                active={ activeView === VIEW_TYPE.MAIN.CONTROL && controlActiveView === VIEW_TYPE.CONTROL_DETAIL.DATA_MANAGEMENT }
                                tabIndex="0"
                                ariaLabel="Data Management"
                                onClick={ () => requestNavigate(() => {
                                    props.updateMainView(VIEW_TYPE.MAIN.CONTROL);
                                    props.updateControlDetailView(VIEW_TYPE.CONTROL_DETAIL.DATA_MANAGEMENT);
                                }) }>
                                <FcDataConfiguration size={ 32 } />
                            </CustomButton>
                        </NavItem>
                    )}
                    <NavItem>
                        <CustomButton
                            className="header__btn header__dashboard rounded-0 px-3"
                            active={ activeView === VIEW_TYPE.MAIN.DASHBOARD }
                            tabIndex="0"
                            ariaLabel="System Health Dashboard section button"
                            onClick={ () => requestNavigate(() => {
                                props.updateMainView(VIEW_TYPE.MAIN.DASHBOARD);
                                props.updateControlDetailView();
                            }) }>
                            <IoIosStats size={ 32 } />
                        </CustomButton>
                    </NavItem>

                    <NavItem>
                        <CustomButton
                            className="header__btn header__user rounded-0 px-3 position-relative"
                            ariaLabel="User profile button"
                            onClick={ () => setIsUserPopoverOpen(!isUserPopoverOpen) }
                            id="header-user"
                            active={ isUserPopoverOpen }>
                            <MdPerson
                                size={ 30 }
                                role="button"
                                aria-label="User profile icon" />
                        </CustomButton>
                        <Popover placement="bottom" target="header-user" isOpen={ isUserPopoverOpen }>
                            <PopoverHeader>{userProfile.name}</PopoverHeader>
                            <PopoverBody>
                                <p className="m-0 mb-2">{userProfile.userName}</p>
                                { !IS_LOGIN_NOT_REQUIRED && (
                                    <Button
                                        className="btn-info btn-sm mt-2"
                                        onClick={ () => logout() }>
                                        Logout
                                    </Button>
                                )}
                            </PopoverBody>
                        </Popover>
                    </NavItem>
                    <NavItem>
                        <CustomButton
                            className="header__btn header__user rounded-0 px-3 position-relative"
                            ariaLabel="Help information button"
                            onClick={ toggleHelpModal }
                            id="header-help"
                            active={ isHelpModalVisible }
                        >
                            <Icon className="user-manual d-block" icon="user-manual" />
                        </CustomButton>
                        {isHelpModalVisible && (
                            <HelpInformationModal
                                onClose={ toggleHelpModal }
                            />
                        )}
                    </NavItem>
                </Nav>
            </Collapse>
            { process.env.NODE_ENV !== 'production' && (
                <span
                    className="header__environment_indicator">
                    <strong>
                        { process.env.NODE_ENV.toUpperCase() }
                    </strong>
                </span>
            )}
            <CustomModal
                title="Log a disruption"
                isModalOpen={ leaveFormConfirm.open }
                onClose={ closeLeaveFormConfirm }
                returnFocusAfterClose={ false }>
                <LeaveFormConfirmation
                    onKeepEditing={ closeLeaveFormConfirm }
                    onDiscard={ discardAndNavigate }
                />
            </CustomModal>
        </Navbar>
    );
}

Header.propTypes = {
    updateMainView: PropTypes.func.isRequired,
    updateControlDetailView: PropTypes.func.isRequired,
    updateSecondaryPanelView: PropTypes.func.isRequired, // eslint-disable-line
    getStopMessagesAndPermissions: PropTypes.func.isRequired,
    activeView: PropTypes.string.isRequired,
    controlActiveView: PropTypes.string,
    hasAlerts: PropTypes.bool, // eslint-disable-line
    stopMessagesPermissions: PropTypes.array.isRequired,
    userProfile: PropTypes.shape({
        name: PropTypes.string.isRequired,
        userName: PropTypes.string.isRequired,
        roles: PropTypes.array.isRequired,
    }).isRequired,
    userPermissions: PropTypes.shape({
        controlRoutesView: PropTypes.bool.isRequired,
        controlBlocksView: PropTypes.bool.isRequired,
        controlStopMessagingView: PropTypes.bool.isRequired,
    }).isRequired,
    resetRealtimeToDefault: PropTypes.func.isRequired,
    useRecurringCancellationsGridView: PropTypes.bool.isRequired,
    updateActiveControlEntityId: PropTypes.func.isRequired,
    activeControlEntityId: PropTypes.string,
    queryParams: PropTypes.object,
    goToNotificationsView: PropTypes.func.isRequired,
    openCreateIncident: PropTypes.func.isRequired,
    openCreateDisruption: PropTypes.func.isRequired,
    updateIsDisruptionFormModified: PropTypes.func.isRequired,
    useBusPriorityDataManagement: PropTypes.bool.isRequired,
    useParentChildIncident: PropTypes.bool.isRequired,
};

Header.defaultProps = {
    hasAlerts: false,
    controlActiveView: '',
    activeControlEntityId: '',
    queryParams: null,
};

export default connect(
    state => ({
        activeView: getActiveMainView(state),
        activeSecondaryPanelView: getActiveSecondaryPanelView(state),
        controlActiveView: getActiveControlDetailView(state),
        userProfile: getUserProfile(state),
        userPermissions: getUserPermissions(state),
        hasAlerts: isAlertsEmpty(state),
        stopMessagesPermissions: getStopMessagesPermissions(state),
        useRecurringCancellationsGridView: useRecurringCancellationsGridView(state),
        activeControlEntityId: getActiveControlEntityId(state),
        queryParams: getQueryParams(state),
        useBusPriorityDataManagement: useBusPriorityDataManagement(state),
        useParentChildIncident: useParentChildIncident(state),
    }),
    {
        resetRealtimeToDefault,
        updateMainView,
        updateControlDetailView,
        updateSecondaryPanelView,
        getStopMessagesAndPermissions,
        updateActiveControlEntityId,
        goToNotificationsView,
        openCreateIncident,
        openCreateDisruption,
        updateIsDisruptionFormModified,
    },
)(Header);
